import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateUserData } from '../app/auth/authSlice';
import userDataService from '../services/userDataService';

const STALE_MS = 60 * 60 * 1000; // 1 hour

/**
 * Custom hook to safely access profile and settings data
 * Handles loading states and retries to prevent "data not available" errors
 */
export const useProfileAndSettings = () => {
  const dispatch = useDispatch();
  const { profileData, settingsData, isAuthenticated, lastDataRefresh } = useSelector(state => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const loadingRef = useRef(false);

  const loadUserData = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      setIsLoading(true);
      const userData = await userDataService.fetchUserData();
      dispatch(updateUserData(userData));
      setHasInitiallyLoaded(true);
    } catch (error) {
      console.error('Failed to load user data:', error);
      setHasInitiallyLoaded(true);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const hasAnyData = !!(profileData || settingsData);
    const isDataStale = lastDataRefresh
      ? (Date.now() - new Date(lastDataRefresh).getTime()) > STALE_MS
      : true;

    // Avoid tight loops when refresh completes but profile/settings remain empty:
    // if we already refreshed recently and still have no data, stop retrying.
    if (!hasAnyData && lastDataRefresh && !isDataStale) {
      setHasInitiallyLoaded(true);
      return;
    }

    if ((!hasAnyData || isDataStale) && !loadingRef.current) {
      loadUserData();
    } else if (hasAnyData) {
      setHasInitiallyLoaded(true);
    }
  }, [isAuthenticated, profileData, settingsData, lastDataRefresh, loadUserData]);

  return {
    profileData,
    settingsData,
    isLoading,
    hasInitiallyLoaded,
    refreshData: loadUserData,
    // Safe getters that return defaults if data not available
    getCompCurrency: () => profileData?.compCurrency || 'GBP',
    getCompTimeZone: () => profileData?.compTimeZone ?? profileData?.CompTimeZone ?? 'UTC',
    getCompUTCTimeZone: () => profileData?.compUTCTimeZone ?? profileData?.CompUTCTimeZone ?? '',
    getVATRate: () => settingsData?.s_VAT || 0,
    isTaxEnabled: () => settingsData?.isTaxEnabled || false,
  };
};

export default useProfileAndSettings;
